import React, { useRef, useState } from "react";
import axios from "axios";
import { Upload, Image as ImageIcon, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MediaField — single-image picker with upload + URL input + library pick.
 * Emits a MediaAsset shape `{ src, alt, caption?, credit? }` (or null).
 */
export function MediaField({ label, value, onChange, testid, aspect = "aspect-[16/9]" }) {
  const [uploading, setUploading] = useState(false);
  const [showLib, setShowLib] = useState(false);
  const [libItems, setLibItems] = useState([]);
  const fileRef = useRef();

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/media/upload`, fd, { withCredentials: true });
      onChange({ src: r.data.url, alt: value?.alt || file.name, caption: value?.caption, credit: value?.credit });
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const openLibrary = async () => {
    try {
      const r = await axios.get(`${API}/admin/media`, { withCredentials: true });
      setLibItems(r.data || []);
      setShowLib(true);
    } catch (e) {
      toast.error("Failed to load library");
    }
  };

  const pickFromLibrary = (m) => {
    onChange({ src: m.url, alt: value?.alt || m.original_filename || "Image", caption: value?.caption, credit: value?.credit });
    setShowLib(false);
  };

  const setField = (k, v) => onChange({ ...(value || { src: "", alt: "" }), [k]: v });

  return (
    <div className="border border-[#343437] p-4 space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label}</span>
        {value?.src && (
          <button type="button" onClick={() => onChange(null)} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1" data-testid={`${testid}-clear`}>
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {value?.src ? (
        <div className={`relative bg-[#0B0B0C] border border-[#343437] overflow-hidden ${aspect}`}>
          <img src={value.src} alt={value.alt || ""} className="absolute inset-0 w-full h-full object-cover" />
        </div>
      ) : (
        <div className={`bg-[#0B0B0C] border border-dashed border-[#343437] ${aspect} flex items-center justify-center text-[#6E6E6B] text-sm`}>
          <ImageIcon size={20} />
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="border border-[#343437] px-3 py-2 text-xs tracking-widest uppercase text-[#FAF9F6] hover:border-[#D8A936] hover:text-[#D8A936] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid={`${testid}-upload`}>
          {uploading ? <><Loader2 size={12} className="animate-spin" /> Uploading</> : <><Upload size={12} /> Upload</>}
        </button>
        <button type="button" onClick={openLibrary} className="border border-[#343437] px-3 py-2 text-xs tracking-widest uppercase text-[#FAF9F6] hover:border-[#D8A936] hover:text-[#D8A936] transition-colors inline-flex items-center justify-center gap-2" data-testid={`${testid}-library`}>
          <ImageIcon size={12} /> Library
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />

      {value?.src && (
        <div className="space-y-2 pt-2 border-t border-[#343437]">
          <label className="block text-xs">
            <span className="eyebrow block mb-1">Alt text (required)</span>
            <input
              type="text"
              value={value.alt || ""}
              onChange={(e) => setField("alt", e.target.value)}
              className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2 text-sm outline-none focus:border-[#D8A936]"
              data-testid={`${testid}-alt`}
              required
            />
          </label>
          <label className="block text-xs">
            <span className="eyebrow block mb-1">Caption (optional)</span>
            <input
              type="text"
              value={value.caption || ""}
              onChange={(e) => setField("caption", e.target.value)}
              className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2 text-sm outline-none focus:border-[#D8A936]"
              data-testid={`${testid}-caption`}
            />
          </label>
          <label className="block text-xs">
            <span className="eyebrow block mb-1">Credit (optional)</span>
            <input
              type="text"
              value={value.credit || ""}
              onChange={(e) => setField("credit", e.target.value)}
              className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2 text-sm outline-none focus:border-[#D8A936]"
              data-testid={`${testid}-credit`}
            />
          </label>
        </div>
      )}

      {showLib && (
        <div className="fixed inset-0 z-[90] bg-[#0B0B0C]/95 flex items-center justify-center p-4" onClick={() => setShowLib(false)}>
          <div className="bg-[#151517] border border-[#343437] w-full max-w-4xl max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">Media library</p>
              <button type="button" onClick={() => setShowLib(false)} className="text-[#A9A9A6] hover:text-[#FAF9F6]" aria-label="Close"><X size={18} /></button>
            </div>
            {libItems.length === 0 ? (
              <p className="text-[#A9A9A6] text-sm">No uploads yet. Upload one via the button above.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {libItems.map((m) => (
                  <button key={m.id} type="button" onClick={() => pickFromLibrary(m)} className="border border-[#343437] hover:border-[#D8A936] transition-colors group text-left" data-testid={`lib-item-${m.id}`}>
                    <div className="aspect-square bg-[#0B0B0C] overflow-hidden">
                      <img src={m.url} alt={m.original_filename} className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />
                    </div>
                    <p className="p-2 text-[10px] text-[#A9A9A6] truncate">{m.original_filename}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * GalleryField — ordered list of MediaAssets, each row with add-remove-reorder controls.
 *
 * Each item carries a client-side `_uid` for React keys — critical because
 * users reorder items and index-based keys break identity across a reorder
 * (form state and file-picker state would collapse to the wrong row).
 * `_uid` is stripped by the backend via `MediaAsset.ConfigDict(extra="ignore")`.
 */
const genUid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `uid-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const withUid = (item) => (item && item._uid ? item : { ...(item || {}), _uid: genUid() });

export function GalleryField({ label, value, onChange, testid }) {
  const items = (Array.isArray(value) ? value : []).map(withUid);
  const addItem = () => onChange([...items, { src: "", alt: "", _uid: genUid() }]);
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const updateItem = (i, next) => {
    const copy = [...items];
    copy[i] = { ...(next || { src: "", alt: "" }), _uid: items[i]._uid };
    onChange(copy);
  };
  const moveUp = (i) => {
    if (i === 0) return;
    const copy = [...items];
    [copy[i - 1], copy[i]] = [copy[i], copy[i - 1]];
    onChange(copy);
  };
  const moveDown = (i) => {
    if (i === items.length - 1) return;
    const copy = [...items];
    [copy[i + 1], copy[i]] = [copy[i], copy[i + 1]];
    onChange(copy);
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label} · {items.length}</span>
        <button type="button" onClick={addItem} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid={`${testid}-add`}>
          + Add image
        </button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-[#A9A9A6] italic border border-dashed border-[#343437] p-4 text-center">
          Empty — no gallery renders on the case study page (§3.2). Add an image with "Add image" above.
        </p>
      ) : (
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={item._uid} className="border border-[#343437] p-3 space-y-3" data-testid={`${testid}-item-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] tracking-[0.18em] uppercase text-[#A9A9A6]">Item {i + 1}</span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => moveUp(i)} disabled={i === 0} className="text-xs px-2 py-1 border border-[#343437] hover:border-[#D8A936] disabled:opacity-30" data-testid={`${testid}-up-${i}`}>↑</button>
                  <button type="button" onClick={() => moveDown(i)} disabled={i === items.length - 1} className="text-xs px-2 py-1 border border-[#343437] hover:border-[#D8A936] disabled:opacity-30" data-testid={`${testid}-down-${i}`}>↓</button>
                  <button type="button" onClick={() => removeItem(i)} className="text-xs px-2 py-1 border border-[#343437] hover:border-red-400 hover:text-red-400" data-testid={`${testid}-remove-${i}`}>Remove</button>
                </div>
              </div>
              <MediaField label={`Image ${i + 1}`} value={item} onChange={(next) => updateItem(i, next || { src: "", alt: "" })} testid={`${testid}-media-${i}`} aspect="aspect-[3/2]" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * ChaptersField — ordered list of case-study chapters. Each chapter is
 * `{ n, title, body, image?, link_label?, link_href? }`.
 * Uses stable client-side `_uid` for keys — same rationale as GalleryField.
 */
export function ChaptersField({ label, value, onChange, testid }) {
  const items = (Array.isArray(value) ? value : []).map(withUid);
  const addItem = () => onChange([...items, { n: String((items.length + 1)).padStart(2, "0"), title: "", body: "", image: null, _uid: genUid() }]);
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const updateItem = (i, patch) => {
    const copy = [...items];
    copy[i] = { ...copy[i], ...patch };
    onChange(copy);
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label} · {items.length}</span>
        <button type="button" onClick={addItem} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid={`${testid}-add`}>
          + Add chapter
        </button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-[#A9A9A6] italic border border-dashed border-[#343437] p-4 text-center">
          No chapters — page falls back to the legacy narratives file (if present) or renders none.
        </p>
      ) : (
        <div className="space-y-4">
          {items.map((c, i) => (
            <div key={c._uid} className="border border-[#343437] p-4 space-y-3" data-testid={`${testid}-item-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] tracking-[0.18em] uppercase text-[#A9A9A6]">Chapter {i + 1}</span>
                <button type="button" onClick={() => removeItem(i)} className="text-xs px-2 py-1 border border-[#343437] hover:border-red-400 hover:text-red-400" data-testid={`${testid}-remove-${i}`}>Remove</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="block">
                  <span className="eyebrow block mb-1">Number</span>
                  <input type="text" value={c.n || ""} onChange={(e) => updateItem(i, { n: e.target.value })} placeholder="01" className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2 text-sm outline-none focus:border-[#D8A936]" />
                </label>
                <label className="block md:col-span-2">
                  <span className="eyebrow block mb-1">Title</span>
                  <input type="text" value={c.title || ""} onChange={(e) => updateItem(i, { title: e.target.value })} className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2 text-sm outline-none focus:border-[#D8A936]" />
                </label>
              </div>
              <label className="block">
                <span className="eyebrow block mb-1">Body</span>
                <textarea rows={4} value={c.body || ""} onChange={(e) => updateItem(i, { body: e.target.value })} className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2 text-sm outline-none focus:border-[#D8A936] resize-none" />
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="eyebrow block mb-1">Link label</span>
                  <input type="text" value={c.link_label || ""} onChange={(e) => updateItem(i, { link_label: e.target.value })} placeholder="Read more" className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2 text-sm outline-none focus:border-[#D8A936]" />
                </label>
                <label className="block">
                  <span className="eyebrow block mb-1">Link href</span>
                  <input type="text" value={c.link_href || ""} onChange={(e) => updateItem(i, { link_href: e.target.value })} placeholder="/services" className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2 text-sm outline-none focus:border-[#D8A936]" />
                </label>
              </div>
              <MediaField label={`Chapter ${i + 1} image (optional)`} value={c.image} onChange={(next) => updateItem(i, { image: next })} testid={`${testid}-image-${i}`} aspect="aspect-[16/9]" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
